import { ExternalDashboardType, OrganizationType } from 'generated/graphql';
import DonationsIcon from 'assets/icons/dashboards-donations.svg';
import DonationsReportIcon from 'assets/icons/dashboards-donations-report.svg';
import CorporateDonationsAndGrantsIcon from 'assets/icons/dashboards-corporate-donations-and-grants.svg';
import VolunteeringIcon from 'assets/icons/dashboards-volunteering.svg';
import EngagementIcon from 'assets/icons/dashboards-engagement.svg';
import AdvancedEngagementIcon from 'assets/icons/dashboards-advanced-engagement.svg';
import ActionsIcon from 'assets/icons/dashboards-actions.svg';
import CommunitiesIcon from 'assets/icons/dashboards-communities.svg';
import EngageESGIcon from 'assets/icons/dashboards-engage-esg.svg';
import ImpactOverviewIcon from 'assets/icons/dashboards-impact-overview.svg';
import GivingSeasonSpotlightIcon from 'assets/icons/dashboards-giving-season-spotlight.svg';
import BadgesIcon from 'assets/icons/dashboards-badges.svg';
import ManagersSpotlightIcon from 'assets/icons/dashboards-managers-spotlight.svg';
import CampaignsIcon from 'assets/icons/dashboards-campaigns.svg';
import StoresSpotlightIcon from 'assets/icons/dashboards-store-spotlight.svg';
import SDGsIcon from 'assets/icons/dashboards-sdgs.svg';
import YearEndImpactIcon from 'assets/icons/dashboards-yearend-impact.svg';
import GrantsIcon from 'assets/icons/dashboards-grants.svg';
import NonVolunteeringIcon from 'assets/icons/dashboards-non-volunteering.svg';
import VolunteeringReportIcon from 'assets/icons/dashboards-volunteering-report.svg';
import CustomDashboardIcon from 'assets/icons/deed-logo-gradient.svg';
import User from 'entities/user/model';

type ConditionalContent<T> = T | ((user: User) => T);

interface DashboardDefinition {
  icon: string;
  family?: string;
  type: ExternalDashboardType;
  title: string;
  text: ConditionalContent<string>;
  beta?: ConditionalContent<boolean>;
  sortOrder?: number;
}

const dashboards: DashboardDefinition[] = [
  {
    icon: DonationsReportIcon,
    type: ExternalDashboardType.DonationsReport,
    title: 'Donations Report Dashboard',
    text: 'Filter and export Deed’s robust donations report.',
  },
  {
    icon: DonationsIcon,
    type: ExternalDashboardType.Donations,
    title: 'Donations Dashboard',
    text: (user) => {
      if (user.organization.type === OrganizationType.Company) {
        return 'Explore your employee and corporate donations data with key metrics and visualizations.';
      }
      return 'Explore donations data with key metrics and visualizations.';
    },
  },
  {
    icon: CorporateDonationsAndGrantsIcon,
    type: ExternalDashboardType.CorporateDonationsAndGrants,
    title: 'Corporate Donations and Grants',
    text: "Manage corporate giving with a focus on your company's corporate donations and grants.",
  },
  {
    icon: VolunteeringIcon,
    type: ExternalDashboardType.Volunteering,
    title: 'Volunteering Dashboard',
    text: (user) => {
      if (user.organization.type === OrganizationType.Company) {
        return "Explore your employees' volunteering data with key metrics and visualizations.";
      }
      return 'Explore volunteering data with key metrics and visualizations';
    },
  },
  {
    icon: VolunteeringReportIcon,
    type: ExternalDashboardType.VolunteeringReport,
    title: 'Volunteering Report Dashboard',
    text: "Filter and export Deed's robust volunteering report.",
  },
  {
    icon: NonVolunteeringIcon,
    type: ExternalDashboardType.NonVolunteering,
    title: 'Events Dashboard',
    text: "Explore your employees' event participation data with key metrics and visualization.",
  },
  {
    icon: EngagementIcon,
    type: ExternalDashboardType.Engagement,
    title: 'Engagement Dashboard',
    text: "Explore your employees' log-in metrics with key metrics and visualizations.",
  },
  {
    icon: ActionsIcon,
    type: ExternalDashboardType.Actions,
    title: 'Actions Dashboard',
    text: "See the positive change you're making through your employees' Actions.",
  },
  {
    icon: CommunitiesIcon,
    type: ExternalDashboardType.Communities,
    title: 'Communities Dashboard',
    text: 'See employee engagement and impact through participation in Communities.',
  },
  {
    icon: EngageESGIcon,
    type: ExternalDashboardType.EngageEsg,
    title: 'EngageESG Dashboard',
    text: 'Track employee and corporate efforts across Environmental, Social, and Governance-related deeds.',
  },
  {
    icon: ImpactOverviewIcon,
    type: ExternalDashboardType.ImpactOverview,
    title: 'Impact Overview Dashboard',
    text: 'Capture key donations, volunteering, and engagement data in one quick view.',
  },
  {
    icon: GivingSeasonSpotlightIcon,
    type: ExternalDashboardType.GivingSeasonSpotlight,
    title: 'Giving Season Spotlight',
    text: 'Focus on key metrics during Giving Season and GivingTuesday.',
  },
  {
    icon: BadgesIcon,
    type: ExternalDashboardType.Badges,
    title: 'Badges Dashboard',
    text: "Celebrate your employees' accomplishments with data showing their badges earned.",
  },
  {
    icon: ManagersSpotlightIcon,
    type: ExternalDashboardType.ManagersSpotlight,
    title: 'Managers Spotlight Dashboard',
    text: 'View team-level engagement and impact, rolled up by Manager.',
  },
  {
    icon: StoresSpotlightIcon,
    type: ExternalDashboardType.StoresSpotlight,
    title: 'Stores Spotlight Dashboard',
    text: 'View team-level engagement and impact, rolled up by Store.',
  },
  {
    icon: SDGsIcon,
    type: ExternalDashboardType.Sdgs,
    title: 'Sustainable Development Goals (SDGs) Dashboard',
    text: 'Track engagement and impact across the 17 Sustainable Development Goals.',
  },
  {
    icon: YearEndImpactIcon,
    type: ExternalDashboardType.Yearendimpact,
    title: 'Year End Impact',
    text: 'Visual snapshot of corporate and employee giving, volunteering, and nonprofit partnerships.',
  },
  {
    icon: GrantsIcon,
    type: ExternalDashboardType.Grants,
    title: 'Grants Dashboard',
    text: 'Explore your grants program(s) key data visualizations.',
  },
  {
    icon: CampaignsIcon,
    type: ExternalDashboardType.Campaigns,
    title: 'Campaigns Dashboard',
    text: 'Gain insight across campaigns and deep dive into specific campaigns.',
  },
  {
    icon: AdvancedEngagementIcon,
    type: ExternalDashboardType.AdvancedEngagement,
    title: 'Advanced Engagement Dashboard',
    text: 'View advanced engagement statistics.',
  },
  {
    icon: CustomDashboardIcon,
    type: ExternalDashboardType.Custom,
    title: 'Custom Dashboard',
    text: 'Custom dashboard for your organization.',
  },
];

const processConditionalContent = <T>(content: ConditionalContent<T>, user: User): T => {
  if (typeof content === 'function') {
    // @NOTE-BG: For some reason (maybe beacause the active version is old) typescript can't infer
    // the correct type after the type check above. So I have to "force" it.
    return (content as (user: User) => T)(user);
  }
  return content;
};

const getDashboards = (user: User) =>
  dashboards.map((dashboard) => ({
    ...dashboard,
    text: processConditionalContent(dashboard.text, user),
    beta: dashboard.beta ? processConditionalContent(dashboard.beta, user) : false,
  }));

// export only enabledDashboards
export { DashboardDefinition, getDashboards };
